import { logout } from 'actions'
import { IS_MAIN_COMPANY } from 'config'
import { AccountType, userHasAccountType } from 'config/user.type.constants'
import { usePipelineViewSync } from 'hooks/usePipelineViewSync'
import jwt from 'jwt-decode'
import CreditDenial from 'pages/Loan/CreditDenial'
import { lazy } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Redirect, Route, Switch, useLocation } from 'react-router-dom'
import { PermissionGate } from 'utils/PermissionGate'

const FeeWorkSheetPage = lazy(() => import('pages/Loan/FeeWorkSheet'))
const LoanStructure = lazy(() => import('pages/LoanStructure'))
const AddAppraisalInformationV2 = lazy(() => import('pages/Loan/OrderAppraisals/AddAppraisalInformationV2'))
const AppraisalInformation = lazy(() => import('pages/Loan/Underwriting/AppraisalInformation/index'))
const BudgetScopeOfWork = lazy(() => import('pages/Loan/Budget'))
const ByteproLogs = lazy(() => import('pages/Loan/ByteproLogs'))
const ClosingCalendar = lazy(() => import('pages/Loan/ClosingCalendar'))
const ClosingScreen = lazy(() => import('pages/Loan/ClosingScreen'))
const ContractorApplication = lazy(() => import('pages/Loan/ContractorApplication'))
const ContractorApplications = lazy(() => import('pages/Loan/ContractorApplications'))
const Dataverify = lazy(() => import('pages/Loan/Underwriting/Dataverify/Dataverify'))
const DocumentGenie = lazy(() => import('pages/Loan/DocumentGenie/DocumentGenie'))
const ExpirationDates = lazy(() => import('pages/Loan/Underwriting/ExpirationDates'))
const Funding = lazy(() => import('pages/Loan/Funding'))
const Invoices = lazy(() => import('pages/Loan/Invoice/Invoices'))
const LoanChangeRequest = lazy(() => import('pages/Loan/LoanChangeRequest'))
const LoanActions = lazy(() => import('pages/Loan/LoanActions'))
const LoanInTake = lazy(() => import('pages/Loan/LoanInTake'))
const Notes = lazy(() => import('pages/Loan/Servicing/Notes/Notes'))
const Parties = lazy(() => import('pages/Loan/Parties'))
const PostClosing = lazy(() => import('pages/Loan/PostClosing'))
const PurchaseAdvice = lazy(() => import('pages/Loan/PostClosing/PurchaseAdvice'))
const PurchaseAdviceSeller = lazy(() => import('pages/Loan/PostClosing/PurchaseAdviceSeller'))
const ReportDetails = lazy(() => import('pages/Loan/Reports/ReportDetails'))
const Reports = lazy(() => import('pages/Loan/Reports/Reports'))
const ReviewForm = lazy(() => import('pages/Loan/Underwriting/ReviewForm/ReviewForm'))
const SecondaryMarketingScreen = lazy(() => import('pages/Loan/SecondaryMarketing/SecondaryMarketingScreen'))
const Servicing = lazy(() => import('pages/Loan/Servicing/Servicing'))
const SharedNexysLoan = lazy(() => import('pages/SharedNexysLoan'))
const ServicingAch = lazy(() => import('pages/Servicing/ACH'))
const PaymentChecks = lazy(() => import('pages/Servicing/PaymentChecks'))
const UsioPortal = lazy(() => import('pages/UsioPortal'))
const ServicingStatements = lazy(() => import('pages/Servicing/Statements'))
const ServicingTaxForms = lazy(() => import('pages/Servicing/TaxForms'))
const TaxFilingReview = lazy(() => import('pages/Tax/FilingReview'))
const TaxFilers = lazy(() => import('pages/Tax/Filers'))
const ServicingCreditReporting = lazy(() => import('pages/Servicing/CreditReporting'))
const ServicingTextMessages = lazy(() => import('pages/Servicing/TextMessages'))
const OnlinePayments = lazy(() => import('pages/Servicing/OnlinePayments'))
const PaymentDefaults = lazy(() => import('pages/Servicing/PaymentDefaults'))
const ArmAdjustments = lazy(() => import('pages/Servicing/ArmAdjustments'))
const LoanImport = lazy(() => import('pages/Servicing/LoanImport'))
const PaymentImport = lazy(() => import('pages/Servicing/PaymentImport'))
const NsfImport = lazy(() => import('pages/Servicing/NsfImport'))
const Collections = lazy(() => import('pages/Servicing/Collections'))
const Extensions = lazy(() => import('pages/Servicing/Extensions'))
const Foreclosure = lazy(() => import('pages/Servicing/Foreclosure'))
const PrintMail = lazy(() => import('pages/Servicing/PrintMail'))
const Reo = lazy(() => import('pages/Loan/Reo/Reo'))
const Fci = lazy(() => import('pages/Loan/Fci/index'))
const LightningDocs = lazy(() => import('pages/Loan/LightningDocs/index'))
const AutomateLoanDocs = lazy(() => import('pages/Loan/AutomateLoanDocs/index'))
const GoDocs = lazy(() => import('pages/Loan/GoDocs/index'))
const Underwriting = lazy(() => import('pages/Loan/Underwriting/Underwriting'))
const UnderwritingSummary = lazy(() => import('pages/Loan/Underwriting/UnderwritingSummary'))
const Tasks = lazy(() => import('pages/Tasks/tasks'))
const AiProjects = lazy(() => import('pages/AiProjects'))
const AiProjectWorkspace = lazy(() => import('pages/AiProjects/ProjectWorkspace'))
const LoanAiProject = lazy(() => import('pages/Loan/LoanAiProjectPage'))
const Appraisals = lazy(() => import('pages/Appraisals'))
const Reminders = lazy(() => import('pages/Reminders'))
const Calendar = lazy(() => import('pages/Calendar'))
const LoanOverview = lazy(() => import('pages/LoanOverview'))
const Pipeline = lazy(() => import('pages/Pipeline'))
const BatchImport = lazy(() => import('pages/Pipeline/BatchImport'))
const Ratesheet = lazy(() => import('pages/Ratesheet'))
const AdminTools = lazy(() => import('pages/Admin/AdminTools/AdminTools'))
const ConditionsAndTemplates = lazy(() => import('pages/Admin/ConditionsAndTemplates/ConditionsAndTemplates'))
const ManageAccountings = lazy(() => import('pages/Admin/Accounting/ManageAccountings'))
const VendorTrustFile = lazy(() => import('pages/Admin/Accounting/VendorTrustFile/VendorTrustFile'))
const Mails = lazy(() => import('pages/Admin/Mails'))
const NationalFlood = lazy(() => import('pages/Admin/Vendors/NationalFlood/NationalFlood'))
const UserActivity = lazy(() => import('pages/Admin/UserActivity'))
const Vendors = lazy(() => import('pages/Admin/Vendors'))
const BorrowerDetails = lazy(() => import('pages/Borrower/ManageBorrowers/BorrowerDetails'))
const ManageBorrowers = lazy(() => import('pages/Borrower/ManageBorrowers/ManageBorrowers'))
const EditProfile = lazy(() => import('pages/Auth/EditProfile'))
const AccountDetails = lazy(() => import('pages/ManageAccounts/AccountDetails/AccountDetails'))
const ManageAccounts = lazy(() => import('pages/ManageAccounts/ManageAccounts'))
const ManageTeams = lazy(() => import('pages/ManageTeams/ManageTeams'))
const ManageUserGroups = lazy(() => import('pages/ManageUserGroups/ManageUserGroups'))
// The one investor directory. It replaced the two screens that used to sit side by side, Manage
// Investors (loan purchasers) and Manage Participants (loan participants).
const ManageInvestors = lazy(() => import('pages/Investors/Manage/ManageInvestors'))
const ManageLoanScenarios = lazy(() => import('pages/Loan/ManageLoanScenarios/ManageLoanScenarios'))
const ManageBorrowingBases = lazy(() => import('pages/ManageBorrowingBases'))
const Billing = lazy(() => import('pages/Billing'))
const ExportHistory = lazy(() => import('pages/ManageAccounts/ExportHistory'))
const AmortizationSchedule = lazy(() => import('pages/Loan/AmortizationSchedule/AmortizationSchedule'))
const MortgageFunds = lazy(() => import('pages/MortgageFunds/MortgageFunds'))
const InvestorOnboardingQueue = lazy(() => import('pages/InvestorOnboarding/Admin/InvestorOnboardingQueue'))
const FundDetail = lazy(() => import('pages/MortgageFunds/FundDetail'))
const LenderTrustFile = lazy(() => import('pages/Participants/TrustFile/LenderTrustFile'))
const ApplicationTemplate = lazy(() => import('pages/Admin/ConditionsAndTemplates/ApplicationEmailTemplate/detail'))
const Hmda = lazy(() => import('pages/Loan/HMDA/index'))
const LoanEstimatePage1 = lazy(() => import('pages/Loan/LoanEstimatePages/LoanEstimatePage1/index'))
const LoanEstimatePage2 = lazy(() => import('pages/Loan/LoanEstimatePages/LoanEstimatePage2/index'))
const LoanEstimatePage3 = lazy(() => import('pages/Loan/LoanEstimatePages/LoanEstimatePage3/index'))
const ClosingDisclosurePage1 = lazy(() => import('pages/Loan/ClosingDisclosurePage/ClosingDisclosurePage1/index'))
const ClosingDisclosureAddendum = lazy(() => import('pages/Loan/ClosingDisclosurePage/ClosingDisclosureAddendum/index'))
const BorrowerDrawRequest = lazy(() => import('pages/Borrower/DrawRequest/BorrowerDrawRequest'))
const BorrowingBase = lazy(() => import('pages/Loan/Servicing/BorrowingBase/index'))
const ConfigurablePage = lazy(() => import('pages/Loan/ConfigurablePage/ConfigurablePage'))
const UnderwritingLinkPage = lazy(() => import('pages/Loan/UnderwritingLinkPage/UnderwritingLinkPage'))
const VendorPortal = lazy(() => import('pages/VendorPortal'))
const VendorLoanOverview = lazy(() => import('pages/VendorPortal/LoanOverview/VendorLoanOverview'))
const VendorDocuments = lazy(() => import('pages/VendorPortal/Documents/VendorDocuments'))

const routes = (
  <Switch>
    <Route exact path="/registrations">
      <PermissionGate permission={'User'} pageId={['manage_users', 'registrations']}>
        <ManageAccounts />
      </PermissionGate>
    </Route>

    <Route exact path="/registrations/exportHistory">
      <PermissionGate permission={'User'} pageId={['manage_users', 'registrations']}>
        <ExportHistory />
      </PermissionGate>
    </Route>

    <Route exact path="/registrations/:userId">
      <PermissionGate permission={'User'} pageId={['manage_users', 'registrations']}>
        <AccountDetails />
      </PermissionGate>
    </Route>

    <Route exact path="/teams">
      <PermissionGate permission={'Team'} pageId={['manage_users', 'teams']}>
        <ManageTeams />
      </PermissionGate>
    </Route>

    <Route exact path="/user_groups">
      <PermissionGate permission={'User.Group'} pageId={['manage_users', 'user_groups']}>
        <ManageUserGroups />
      </PermissionGate>
    </Route>

    <Route exact path="/user_activity">
      <PermissionGate permission={'User.Activity'} pageId={['manage_users', 'user_activity']}>
        <UserActivity />
      </PermissionGate>
    </Route>
    <Route exact path="/admin_tool/:menu">
      <PermissionGate
        permission={[
          'AdminTools',
          'AdminTools.ApiKeys',
          'AdminTools.Permissions',
          'AdminTools.AppraiserSettlementContractor',
          'AdminTools.Reviews',
        ]}
        pageId={['admin_tool', ':menu']}
      >
        <AdminTools />
      </PermissionGate>
    </Route>
    <Route exact path="/accountings/vendor/:id/trustLedger">
      <PermissionGate permission={['Accounting']} pageId={['manage_accountings']}>
        <VendorTrustFile />
      </PermissionGate>
    </Route>
    <Route exact path="/accountings/:menu">
      <PermissionGate permission={['Accounting']} pageId={['manage_accountings', ':menu']}>
        <ManageAccountings />
      </PermissionGate>
    </Route>
    <Route exact path="/mails">
      <PermissionGate permission={['Mail']}>
        <Mails />
      </PermissionGate>
    </Route>
    <Route exact path="/vendors/:menu">
      <PermissionGate permission={'Vendors'} pageId={['vendors']}>
        <Vendors />
      </PermissionGate>
    </Route>
    <Route exact path="/condition_template/applicationEmailTemplate/:id">
      <PermissionGate permission={'Template'}>
        <ApplicationTemplate />
      </PermissionGate>
    </Route>
    <Route exact path="/condition_template/:menu">
      <PermissionGate permission={'Template'} pageId={['condition_template', ':menu']}>
        <ConditionsAndTemplates />
      </PermissionGate>
    </Route>
    <Route exact path="/servicing/:loan_number">
      <PermissionGate pageId={['pipeline', 'servicing', ':menu']}>
        <Servicing />
      </PermissionGate>
    </Route>
    <Route exact path="/servicing-ach">
      <PermissionGate permission={'Servicing.ACH'}>
        <ServicingAch />
      </PermissionGate>
    </Route>
    <Route exact path="/payment-checks">
      <PermissionGate permission={'Servicing.CheckQueue'}>
        <PaymentChecks />
      </PermissionGate>
    </Route>
    <Route exact path="/usio">
      <PermissionGate permission={'UsioPortal'}>
        <UsioPortal />
      </PermissionGate>
    </Route>
    <Route exact path="/servicing-statements">
      <PermissionGate permission={'Servicing.Statements'}>
        <ServicingStatements />
      </PermissionGate>
    </Route>
    <Route exact path="/servicing-tax-forms">
      <PermissionGate permission={'Servicing.Statements'}>
        <ServicingTaxForms />
      </PermissionGate>
    </Route>
    <Route exact path="/tax-filing">
      <PermissionGate permission={'Servicing.Statements'}>
        <TaxFilingReview />
      </PermissionGate>
    </Route>
    <Route exact path="/tax-filers">
      <PermissionGate permission={'Servicing.Statements'}>
        <TaxFilers />
      </PermissionGate>
    </Route>
    <Route exact path="/credit-reporting">
      <PermissionGate permission={'Servicing.CreditReporting'}>
        <ServicingCreditReporting />
      </PermissionGate>
    </Route>
    <Route exact path="/servicing-text-messages">
      <PermissionGate permission={'Servicing.TextMessages'}>
        <ServicingTextMessages />
      </PermissionGate>
    </Route>
    <Route exact path="/online-payments">
      <PermissionGate permission={'Servicing.OnlinePayments'}>
        <OnlinePayments />
      </PermissionGate>
    </Route>
    <Route exact path="/loan-import">
      <PermissionGate permission={'Servicing.LoanImport'}>
        <LoanImport />
      </PermissionGate>
    </Route>
    <Route exact path="/payment-import">
      <PermissionGate permission={'Servicing.PaymentImport'}>
        <PaymentImport />
      </PermissionGate>
    </Route>
    <Route exact path="/nsf-import">
      <PermissionGate permission={'Servicing.PaymentImport'}>
        <NsfImport />
      </PermissionGate>
    </Route>
    <Route exact path="/payment-defaults">
      <PermissionGate permission={'Servicing.PaymentDefaults'}>
        <PaymentDefaults />
      </PermissionGate>
    </Route>
    <Route exact path="/arm-adjustments">
      <PermissionGate permission={'Servicing.ArmAdjustments'}>
        <ArmAdjustments />
      </PermissionGate>
    </Route>
    <Route exact path="/collections">
      <PermissionGate permission={'Servicing.Collections'}>
        <Collections />
      </PermissionGate>
    </Route>
    <Route exact path="/extensions">
      <PermissionGate permission={'Servicing.Extensions'}>
        <Extensions />
      </PermissionGate>
    </Route>
    <Route exact path="/foreclosure">
      <PermissionGate permission={'Servicing.Foreclosure'}>
        <Foreclosure />
      </PermissionGate>
    </Route>
    <Route exact path="/print-mail">
      <PermissionGate permission={'PrintMail'}>
        <PrintMail />
      </PermissionGate>
    </Route>
    <Route exact path="/reo/:loan_number">
      <Reo />
    </Route>
    <Route exact path="/budget/:loanNumber">
      <PermissionGate permission={'Loan.BudgetScopeWork'} pageId={['pipeline', 'budget']}>
        <BudgetScopeOfWork />
      </PermissionGate>
    </Route>
    <Route exact path="/underwriting/:loan_number">
      <PermissionGate pageId={['pipeline', 'underwriting']}>
        <Underwriting />
      </PermissionGate>
    </Route>
    <Route exact path="/loanInTake/:loan_number">
      <PermissionGate pageId={['pipeline', 'loanInTake']}>
        <LoanInTake />
      </PermissionGate>
    </Route>
    <Route exact path="/bytepro_logs/:loan_number">
      <PermissionGate pageId={['pipeline', 'bytepro_logs']}>
        <ByteproLogs />
      </PermissionGate>
    </Route>
    <Route exact path="/edit_profile">
      <EditProfile />
    </Route>
    <Route exact path="/loan_process/:action/:loan_number">
      <LoanOverview />
    </Route>
    <Route exact path="/pool/:id">
      <LoanStructure />
    </Route>
    <Route exact path="/pool/parties/:id">
      <Parties />
    </Route>
    <Route exact path="/loan_change_request/:loan_number">
      <PermissionGate pageId={['pipeline', 'loan_change_request']}>
        <LoanChangeRequest />
      </PermissionGate>
    </Route>
    <Route exact path="/post_closing/:loan_number">
      <PermissionGate pageId={['pipeline', 'post_closing']}>
        <PostClosing />
      </PermissionGate>
    </Route>
    <Route exact path="/closing_calendar/:loan_number">
      <PermissionGate pageId={['pipeline', 'closing_calendar']}>
        <ClosingCalendar />
      </PermissionGate>
    </Route>
    <Route exact path="/closing_screen/:loan_number">
      <PermissionGate permission={'Loan.ClosingScreen'} pageId={['pipeline', 'closing_screen', 'menu']}>
        <ClosingScreen />
      </PermissionGate>
    </Route>
    <Route exact path="/fee_worksheet/:loan_number">
      <PermissionGate pageId={['pipeline', 'fee_worksheet']}>
        <FeeWorkSheetPage />
      </PermissionGate>
    </Route>
    <Route exact path="/amortization_schedule/:loan_number">
      <PermissionGate permission={'Loan.AmortizationSchedule'} pageId={['pipeline', 'amortization_schedule']}>
        <AmortizationSchedule />
      </PermissionGate>
    </Route>
    <Route exact path="/order_valuation/:loan_number">
      <PermissionGate pageId={['pipeline', 'order_valuation']}>
        <AddAppraisalInformationV2 />
      </PermissionGate>
    </Route>
    <Route exact path="/document_genie/:loan_number">
      <PermissionGate pageId={['pipeline', 'document_genie']}>
        <DocumentGenie />
      </PermissionGate>
    </Route>
    <Route exact path="/review_form/:loan_number">
      <PermissionGate permission={'Loan.Underwriting.Valuation.ReviewForm'} pageId={['pipeline', 'review_form']}>
        <ReviewForm />
      </PermissionGate>
    </Route>
    <Route exact path="/appraisal_information/:loan_number">
      <PermissionGate
        permission={'Loan.Underwriting.AppraisalInformation'}
        pageId={['pipeline', 'appraisal_information']}
      >
        <AppraisalInformation />
      </PermissionGate>
    </Route>
    <Route exact path="/reminders">
      <PermissionGate pageId={['reminders']}>
        <Reminders />
      </PermissionGate>
    </Route>
    <Route exact path="/reminders/:loanNumber">
      <PermissionGate pageId={['reminders']}>
        <Reminders />
      </PermissionGate>
    </Route>
    <Route exact path="/calendar">
      <PermissionGate pageId={['calendar']}>
        <Calendar />
      </PermissionGate>
    </Route>
    <Route exact path="/appraisals">
      <PermissionGate permission={'Loan.Appraisals'} pageId={['appraisals']}>
        <Appraisals />
      </PermissionGate>
    </Route>
    <Route exact path="/expiration_dates/:loan_number">
      <PermissionGate pageId={['expiration_dates']}>
        <ExpirationDates />
      </PermissionGate>
    </Route>
    <Route exact path="/notes/:loan_number">
      <PermissionGate permission={'Loan.Servicing.Notes'} pageId={['notes']}>
        <Notes />
      </PermissionGate>
    </Route>
    <Route exact path="/dataverify/:loan_number">
      <PermissionGate permission={'Loan.Underwriting.Dataverify'}>
        <Dataverify />
      </PermissionGate>
    </Route>
    <Route exact path="/underwriting_summary/:loan_number">
      <PermissionGate permission={'Loan.Underwriting.Summary'} pageId={['underwriting_summary']}>
        <UnderwritingSummary />
      </PermissionGate>
    </Route>
    <Route exact path="/purchase_advice/:loan_number">
      <PermissionGate permission={'Loan.PostClosing.PurchaseAdvice.Buyer'}>
        <PurchaseAdvice />
      </PermissionGate>
    </Route>
    <Route exact path="/purchase_advice_seller/:loan_number">
      <PermissionGate permission={'Loan.PostClosing.PurchaseAdvice.Seller'}>
        <PurchaseAdviceSeller />
      </PermissionGate>
    </Route>
    <Route exact path="/secondary_marketing_screen/:loan_number">
      <PermissionGate permission={'Loan.SecondaryMarketing'} pageId={['pipeline', 'secondary_marketing_screen']}>
        <SecondaryMarketingScreen />
      </PermissionGate>
    </Route>
    <Route exact path="/national_flood/:loan_number">
      <PermissionGate permission={'Loan.NationalFlood'}>
        <NationalFlood />
      </PermissionGate>
    </Route>
    <Route exact path="/reports">
      <PermissionGate permission={'Reports'} pageId={['reports']}>
        <Reports />
      </PermissionGate>
    </Route>
    <Route exact path="/reports/servicing">
      <PermissionGate permission={'Reports'} pageId={['reports']}>
        <Reports origin={1} />
      </PermissionGate>
    </Route>
    <Route exact path="/reports/:id">
      <PermissionGate permission={'Reports'} pageId={['reports']}>
        <ReportDetails />
      </PermissionGate>
    </Route>
    <Route exact path="/pipeline">
      <PermissionGate pageId={['pipeline']}>
        <Pipeline />
      </PermissionGate>
    </Route>
    <Route exact path="/pipeline/batch-import">
      <PermissionGate permission={'Pipeline.View.BatchImport'} pageId={['pipeline']}>
        <BatchImport />
      </PermissionGate>
    </Route>
    <Route exact path="/nexys-shared/:peerId/:loanNumber">
      <PermissionGate pageId={['pipeline']}>
        <SharedNexysLoan />
      </PermissionGate>
    </Route>
    <Route exact path="/ratesheet">
      <PermissionGate pageId={['manage_users', 'ratesheet']}>
        <Ratesheet />
      </PermissionGate>
    </Route>
    <Route exact path="/manageBorrowingBases">
      <PermissionGate permission={'BorrowingBases'}>
        <ManageBorrowingBases />
      </PermissionGate>
    </Route>
    <Route exact path="/billing">
      <PermissionGate permission={'Billing'}>
        <Billing />
      </PermissionGate>
    </Route>
    <Route exact path="/ai-projects">
      <PermissionGate permission={'AiProjects'}>
        <AiProjects />
      </PermissionGate>
    </Route>
    <Route exact path="/ai-projects/:projectId">
      <PermissionGate permission={'AiProjects'}>
        <AiProjectWorkspace />
      </PermissionGate>
    </Route>
    {/* The same workspace, scoped to one loan. Origination screens have no per-loan menu of their
        own, so this route is how the assistant's "open the workspace" link reaches a loan. */}
    <Route exact path="/loan_ai_project/:loan_number">
      <LoanAiProject />
    </Route>
    <Route exact path="/tasks">
      <PermissionGate permission={'Task'} pageId={['tasks']}>
        <Tasks />
      </PermissionGate>
    </Route>
    <Route exact path="/tasks/:projectId">
      <PermissionGate permission={'Task'} pageId={['tasks']}>
        <Tasks />
      </PermissionGate>
    </Route>
    <Route exact path="/tasks/loan/:loanNumber">
      <PermissionGate permission={'Task'} pageId={['tasks']}>
        <Tasks />
      </PermissionGate>
    </Route>
    <Route exact path="/tasks/loan/:loanNumber/:taskNo">
      <PermissionGate permission={'Task'} pageId={['tasks']}>
        <Tasks />
      </PermissionGate>
    </Route>
    <Route exact path="/parties/:loanNumber">
      <PermissionGate permission={['Loan.Party', 'Loan.Party.Insurance.Title']}>
        <Parties />
      </PermissionGate>
    </Route>
    <Route exact path="/funding/:loanNumber">
      <PermissionGate permission={'Loan.Funding'} pageId={['pipeline', 'funding']}>
        <Funding />
      </PermissionGate>
    </Route>
    <Route exact path="/contractor_application/:loanNumber">
      <PermissionGate pageId={['pipeline', 'contractor_application']}>
        <ContractorApplications />
      </PermissionGate>
    </Route>
    <Route exact path="/contractor_application/:no/:loanNumber">
      <PermissionGate pageId={['pipeline', 'contractor_application']}>
        <ContractorApplication />
      </PermissionGate>
    </Route>
    <Route exact path="/invoice/:loanNumber">
      <PermissionGate permission={'Loan.Invoice'} pageId={['pipeline', 'invoice']}>
        <Invoices />
      </PermissionGate>
    </Route>
    <Route exact path="/borrowers">
      <PermissionGate permission={'Borrowers'} pageId={['manage_users', 'borrowers']}>
        <ManageBorrowers />
      </PermissionGate>
    </Route>
    <Route exact path="/borrowers/:borrowerId">
      <PermissionGate permission={'Borrowers'} pageId={['manage_users', 'borrowers']}>
        <BorrowerDetails />
      </PermissionGate>
    </Route>
{/* Either old permission opens the merged screen: a role that could see only one of the two
        it replaced keeps seeing this one rather than silently losing access to the rename. */}
    <Route exact path="/manageInvestors">
      <PermissionGate
        permission={['Investors.Purchases', 'Investors.Servicing']}
        pageId={['manage_users', 'manageInvestors']}
      >
        <ManageInvestors />
      </PermissionGate>
    </Route>
    <Route exact path="/manageInvestors/:id/trustLedger">
      <PermissionGate
        permission={['Investors.Purchases', 'Investors.Servicing']}
        pageId={['manage_users', 'manageInvestors']}
      >
        <LenderTrustFile />
      </PermissionGate>
    </Route>
    {/* The addresses this screen used to live at. Kept so bookmarks and older links still land. */}
    <Route
      exact
      path="/manageParticipants/:id/trustLedger"
      render={({ match }) => <Redirect to={`/manageInvestors/${match.params.id}/trustLedger`} />}
    />
    <Route exact path="/manageParticipants">
      <Redirect to="/manageInvestors" />
    </Route>
    {/* Onboarding is reached through a fund's Onboarding tab; this fund-independent view is kept
        for bookmarks and for looking across every fund at once, and is no longer in the menu.
        Its own prefix, NOT /investorOnboarding/review: the applicant's public wizard owns
        /investorOnboarding/:token, and a staff path underneath it would be read as a token and
        refused. The participant portal learned this the hard way. */}
    <Route exact path="/manageInvestorOnboarding">
      <PermissionGate permission={'Funds'} pageId={['manage_users', 'investorOnboarding']}>
        <InvestorOnboardingQueue />
      </PermissionGate>
    </Route>
    <Route exact path="/mortgageFunds">
      <PermissionGate permission={'Funds'} pageId={['manage_users', 'mortgageFunds']}>
        <MortgageFunds />
      </PermissionGate>
    </Route>
    <Route exact path="/mortgageFunds/:id">
      <PermissionGate permission={'Funds'} pageId={['manage_users', 'mortgageFunds']}>
        <FundDetail />
      </PermissionGate>
    </Route>
    <Route exact path="/loanScenarios">
      <PermissionGate permission={'LoanScenarios'} pageId={['manage_users', 'loanScenarios']}>
        <ManageLoanScenarios />
      </PermissionGate>
    </Route>
    <Route exact path="/fci/:loan_number">
      <PermissionGate pageId={['pipeline', 'fci']}>
        <Fci />
      </PermissionGate>
    </Route>
    <Route exact path="/lightningDocs/:loan_number">
      <PermissionGate pageId={['pipeline', 'lightningDocs']}>
        <LightningDocs />
      </PermissionGate>
    </Route>
    <Route exact path="/automateLoanDocs/:loan_number">
      <PermissionGate pageId={['pipeline', 'automateLoanDocs']}>
        <AutomateLoanDocs />
      </PermissionGate>
    </Route>
    <Route exact path="/goDocs/:loan_number">
      <PermissionGate pageId={['pipeline', 'goDocs']}>
        <GoDocs />
      </PermissionGate>
    </Route>
    <Route exact path="/hmda/:loan_number">
      <Hmda />
    </Route>
    <Route exact path="/loanEstimate/:loan_number">
      <LoanEstimatePage1 />
    </Route>
    <Route exact path="/loanEstimate2/:loan_number">
      <LoanEstimatePage2 />
    </Route>
    <Route exact path="/loanEstimate3/:loan_number">
      <LoanEstimatePage3 />
    </Route>
    <Route exact path="/closingDisclosure1/:loan_number">
      <ClosingDisclosurePage1 />
    </Route>
    <Route exact path="/closingDisclosureAddendum/:loan_number">
      <ClosingDisclosureAddendum />
    </Route>
    <Route exact path="/creditDenial/:loan_number">
      <CreditDenial />
    </Route>
    <Route exact path="/drawRequest/:loanNumber" key={11}>
      <BorrowerDrawRequest forBorrower={false} />
    </Route>
    <Route exact path="/borrowingBase/:loanNumber" key={11}>
      <BorrowingBase />
    </Route>
    <Route exact path="/configurablePage/:pageId/:menuItem/:loanNumber">
      <ConfigurablePage />
    </Route>
    <Route exact path="/configurablePage/:pageId/:loanNumber">
      <ConfigurablePage />
    </Route>
    <Route exact path="/loan_actions/:loanNumber">
      <PermissionGate permission={'Loan.Actions'}>
        <LoanActions />
      </PermissionGate>
    </Route>
    <Route exact path="/uw_link_page/:menuItem/:loanNumber">
      <UnderwritingLinkPage />
    </Route>
    <Route exact path="/vendorPortal/submit/:loanNumber">
      <VendorDocuments />
    </Route>
    <Route exact path="/vendorPortal/overview/:loanNumber">
      <VendorLoanOverview />
    </Route>
    <Route
      exact
      path="/vendorPortal/:loanNumber"
      render={({ match }) => <Redirect to={`/vendorPortal/overview/${match.params.loanNumber}`} />}
    />
    <Route exact path="/vendorPortal">
      <VendorPortal />
    </Route>
    <Route path="*">
      <Redirect to={IS_MAIN_COMPANY ? '/' : '/pipeline'} />
    </Route>
  </Switch>
)

export const ProtectedRoutes = () => {
  const dispatch = useDispatch()
  const auth = useSelector((state: any) => state.auth)
  let success = false

  try {
    const payload: any = jwt(auth.token)
    if (payload.exp * 1000 > Date.now()) {
      success = true
    } else {
      dispatch(logout())
    }
  } catch {}

  try {
    if (auth.profile.temparary && !auth.profile.impersonatedBy) {
      dispatch(logout())
      success = false
    }
  } catch {}

  const location = useLocation()
  const profile = auth.profile

  // Entering servicing / origination switches the pipeline view to match.
  usePipelineViewSync()

  if (success && profile?.temparary && profile?.impersonatedBy) {
    return <Redirect to="/borrowerPipelines" />
  }

  if (success && userHasAccountType(profile, AccountType.VENDOR)) {
    if (!location.pathname.startsWith('/vendorPortal')) {
      return <Redirect to="/vendorPortal" />
    }
    return routes
  }

  if (success) return routes

  return (
    <Redirect
      to={{
        pathname: IS_MAIN_COMPANY ? '/' : '/login',
        state: { from: `${location.pathname}${location.search}` }, // Save the original URL
      }}
    />
  )
}
